/**
 * war_sky_batch — the fleet war's ships in the ground's night sky, COUNTED (space campaign slice L, 2026-10-01; report
 * §10.4 "Ground sky / distant fleet": one compact instanced batch, ≤ 98 silhouettes × 80 B, a ≤ 1 KiB summary at ≤ 1 Hz,
 * "reduce flashes / count detail; retain true active / end / front state").
 *
 * ONE InstancedMesh of a procedural hull silhouette (a long wedge with a spine and a stern block, ~40 triangles), its
 * COUNT FIXED at the 98 capacity (three folds `count > 1` into the render-object cache key — a count that moved would
 * rebuild the shader; the FX-batch law); a slot with no ship is zero-scaled. Per instance: the civ's identity tint over
 * sunlit hull white (Aurelian gold, Borean ice — the same on every UI theme, style.md), the class by size (capital ≫
 * frigate > corvette ≫ gunship), the nose along the heading toward the other wall. Unlit (sunlit up there whatever the
 * ground's hour), fog off, depth-write on (the night-sky bodies' doctrine: R = 3600 m inside the far plane, the cloud
 * field composites over it). ≤ 98 instances is under both backends' uniform-buffer instance limit, so the previous
 * instance matrices are not the frozen attribute path (THE FROZEN MOTION VECTORS law). Placement is in ABSOLUTE
 * coordinates around the player (`atlas.localX/Y/Z`), exactly like the bodies module's station and planets.
 */
import * as THREE from 'three/webgpu';
import { SKY_MIN_THICK, type SkyShip, type Vec3 } from '../night_sky_schedule.js';

export const WAR_SKY_CAPACITY = 98;
const R = 3600;
const HULL_SUN: [number, number, number] = [0.95, 0.86, 0.72];
const TINT: Readonly<Record<0 | 1, [number, number, number]>> = { 0: [0.902, 0.725, 0.353], 1: [0.427, 0.702, 0.949] }; // #e6b95a / #6db3f2

/** The silhouette: unit length along −Z (the nose), a wedge hull + a dorsal spine + a stern block. */
export function silhouetteGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const hull = new THREE.ConeGeometry(0.11, 1, 4, 1);           // a 4-sided spike = a wedge hull
  hull.rotateX(-Math.PI / 2); hull.scale(1, 0.55, 1);
  parts.push(hull);
  const spine = new THREE.BoxGeometry(0.05, 0.08, 0.55); spine.translate(0, 0.06, 0.12);
  parts.push(spine);
  const stern = new THREE.BoxGeometry(0.2, 0.09, 0.12); stern.translate(0, 0, 0.44);
  parts.push(stern);
  // Merge by hand (no BufferGeometryUtils dependency): non-indexed position + normal.
  const pos: number[] = [], nrm: number[] = [];
  for (const g0 of parts) {
    const g = g0.index ? g0.toNonIndexed() : g0;
    pos.push(...(g.getAttribute('position').array as ArrayLike<number> as number[]));
    g.computeVertexNormals();
    nrm.push(...(g.getAttribute('normal').array as ArrayLike<number> as number[]));
    g0.dispose(); if (g !== g0) g.dispose();
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  out.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  out.computeBoundingSphere();
  return out;
}

export class WarSkyBatch {
  readonly mesh: THREE.InstancedMesh;
  private readonly mat: THREE.MeshBasicMaterial;
  private readonly m = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly p = new THREE.Vector3();
  private readonly s = new THREE.Vector3();
  private readonly back = new THREE.Vector3();
  private readonly dS = new THREE.Vector3();
  private readonly tS = new THREE.Vector3();
  private readonly bS = new THREE.Vector3();
  private readonly up = new THREE.Vector3();
  private readonly right = new THREE.Vector3();
  private readonly basis = new THREE.Matrix4();
  private readonly col = new THREE.Color();
  private readonly zero = new THREE.Matrix4().makeScale(0, 0, 0);
  /** How many slots drew ships last update (telemetry). */
  drawn = 0;
  /** The battle's sky direction at the last update (telemetry — a probe aims the camera at it). */
  readonly lastDir = { x: 0, y: -1, z: 0 };
  readonly byClass = { capital: 0, frigate: 0, corvette: 0, gunship: 0 };

  constructor() {
    this.mat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    this.mat.fog = false;
    this.mesh = new THREE.InstancedMesh(silhouetteGeometry(), this.mat, WAR_SKY_CAPACITY);
    this.mesh.name = 'war-sky-batch';
    this.mesh.frustumCulled = false;
    this.mesh.count = WAR_SKY_CAPACITY;                            // FIXED — never moved (the cache-key law)
    for (let i = 0; i < WAR_SKY_CAPACITY; i++) { this.mesh.setMatrixAt(i, this.zero); this.mesh.setColorAt(i, this.col.setRGB(1, 1, 1)); }
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
    this.mesh.visible = false;
  }

  /**
   * Seat the ships: `dir` / `tangent` the battle's sky frame (unit, world), `ships` the layout, `anchor` the player's
   * ABSOLUTE position (the sky bodies' frame), `brightness` 0..1 (the war's envelope × the night).
   */
  update(dir: Vec3, tangent: Vec3, ships: readonly SkyShip[], anchor: Vec3, brightness: number): void {
    const n = Math.min(ships.length, WAR_SKY_CAPACITY);
    const on = brightness > 0.01 && n > 0;
    this.mesh.visible = on;
    if (!on) { this.drawn = 0; return; }
    const d = this.dS.set(dir.x, dir.y, dir.z).normalize();
    this.lastDir.x = d.x; this.lastDir.y = d.y; this.lastDir.z = d.z;
    const tg = this.tS.set(tangent.x, tangent.y, tangent.z).normalize();
    const bt = this.bS.crossVectors(d, tg).normalize();
    this.byClass.capital = 0; this.byClass.frigate = 0; this.byClass.corvette = 0; this.byClass.gunship = 0;
    for (let i = 0; i < WAR_SKY_CAPACITY; i++) {
      if (i >= n) { this.mesh.setMatrixAt(i, this.zero); continue; }
      const sh = ships[i];
      this.byClass[sh.cls]++;
      const w = this.s.copy(d).addScaledVector(tg, sh.u).addScaledVector(bt, sh.v).normalize();
      const px = anchor.x + w.x * R, py = anchor.y + w.y * R, pz = anchor.z + w.z * R;
      // Nose along ±tangent (toward the other wall): local −Z = the nose, so local +Z = −nose; up from the bitangent.
      this.back.copy(tg).multiplyScalar(-sh.face);                               // local +Z
      this.right.crossVectors(bt, this.back).normalize();                        // local +X
      this.up.crossVectors(this.back, this.right).normalize();                   // local +Y (orthogonal)
      this.basis.makeBasis(this.right, this.up, this.back);
      this.q.setFromRotationMatrix(this.basis);
      // The silhouette is ~0.12 of its length across: a small hull is kept at least SKY_MIN_THICK wide (a speck, not
      // nothing — a sub-pixel sliver rasterises to no pixel at all).
      const L = sh.len * R, T = Math.max(L, (SKY_MIN_THICK / 0.12) * R);
      this.m.compose(this.p.set(px, py, pz), this.q, this.s.set(T, T, L));
      this.mesh.setMatrixAt(i, this.m);
      const tint = TINT[sh.side];
      const mixK = sh.cls === 'gunship' ? 0.55 : 0.35;                            // small hulls read by their civ colour
      const b = Math.max(0, Math.min(1.6, brightness * (sh.cls === 'capital' ? 1.1 : 1)));
      this.col.setRGB((HULL_SUN[0] * (1 - mixK) + tint[0] * mixK) * b, (HULL_SUN[1] * (1 - mixK) + tint[1] * mixK) * b, (HULL_SUN[2] * (1 - mixK) + tint[2] * mixK) * b);
      this.mesh.setColorAt(i, this.col);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
    this.drawn = n;
  }

  hide(): void { this.mesh.visible = false; this.drawn = 0; }

  dispose(): void {
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
    this.mat.dispose();
  }
}
